/**
 * Physician Detail Page
 * 
 * Shows detailed information for a single physician
 */

import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { physicianAPI, sharedPhysiciansAPI, apiHelpers } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle,
  Button,
  Label
} from '../components/ui'
import {
  ComposableMap,
  Geographies,
  Geography
} from 'react-simple-maps'
import PhysicianEditModal from '../components/PhysicianEditModal'
import LicenseEditModal from '../components/LicenseEditModal'
import CompensationEditor, { compLabel } from '../components/CompensationEditor'
import SharedDraftBar from '../components/SharedDraftBar'
import useDraftStore, { useSharedDraftStore, SHARED_DIRECTORY_SCOPE } from '../stores/draftStore'

// US states topology for react-simple-maps (using local file)
const geoUrl = "/gz_2010_us_040_00_20m.json"

/**
 * One physician, from the clinic's own collection or (`shared`) core's shared
 * directory, whose drafts go to the shared store and publish as
 * shared-directory releases. Pay only with `showPay` (the Shared Doctors
 * detail page, for editors), never on a clinic's Physicians page.
 */
export function PhysicianDetail({ shared = false, canEdit = false, basePath = '/physicians', showPay = false }) {
  const { id } = useParams()
  const navigate = useNavigate()
  const [physician, setPhysician] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  
  // Modal state
  const [isEditInfoOpen, setIsEditInfoOpen] = useState(false)
  const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false)
  const [editingLicense, setEditingLicense] = useState(null) // { stateCode, data }
  const [isAddLicenseOpen, setIsAddLicenseOpen] = useState(false)

  // Get auth state to check clinic selection
  const { user, currentClinicId, isLoading: authLoading } = useAuthStore()
  const draftStore = shared ? useSharedDraftStore : useDraftStore
  const draftScope = shared ? SHARED_DIRECTORY_SCOPE : currentClinicId
  const sharedDraftProps = shared ? { draftStore: useSharedDraftStore, draftScope: SHARED_DIRECTORY_SCOPE, wholeRecord: true } : {}
  const editable = !shared || canEdit

  useEffect(() => {
    // Load physician if we have a clinic selected OR if user is super admin
    if (shared || currentClinicId || (user?.role === 'super_admin' && !authLoading)) {
      loadPhysician()
    }
  }, [id, currentClinicId, user?.role, authLoading, shared])

  // The draft store persists per (clinic, user), and addChange silently fails to
  // write to localStorage until it knows both. LicenseEditModal initializes on
  // open; deleting happens straight from the table with no modal, so the page
  // has to do it too.
  useEffect(() => {
    if (!user || !draftScope) return
    const { initialize, isInitialized, clinicId: storeClinicId, userId: storeUserId } = draftStore.getState()
    if (!isInitialized || storeUserId !== user.id || storeClinicId !== draftScope) {
      initialize(draftScope, user.id)
    }
  }, [user, draftScope, draftStore])

  /**
   * Stage removal of a state's whole license entry.
   *
   * Modelled as an `update` to the physician with the state key dropped from
   * `licenses`, not as a `delete` change — a delete change removes the whole
   * physician document. releaseService applies updates with `$set`, and
   * `licenses` is a top-level key, so setting the map without this state is what
   * actually removes it on publish.
   *
   * Because it is an ordinary update carrying original_data and new_data, it
   * shows up in drafts and the release diff exactly like any other license edit.
   */
  const handleDeleteLicense = (stateCode) => {
    if (!physician?.licenses?.[stateCode]) return

    const licenseData = physician.licenses[stateCode]
    const stateLicense = licenseData?.state?.license
    const deaLicense = licenseData?.DEA?.license
    const detail = [
      stateLicense ? `state ${stateLicense}` : null,
      deaLicense ? `DEA ${deaLicense}` : null
    ].filter(Boolean).join(', ') || 'no license numbers on file'

    const confirmed = confirm(
      `Remove the ${stateCode} license for ${physician.first_name} ${physician.last_name}?\n\n` +
      `${detail}\n\n` +
      `This is staged as a draft — it is not removed until you publish a release.`
    )
    if (!confirmed) return

    const { [stateCode]: _removed, ...remainingLicenses } = physician.licenses
    const updatedPhysician = { ...physician, licenses: remainingLicenses }

    draftStore.getState().addChange({
      resource_type: 'physician',
      resource_id: physician._id,
      operation: 'update',
      original_data: physician,
      new_data: updatedPhysician,
      description: `Remove ${stateCode} license (${detail}): ${physician.first_name} ${physician.last_name}`
    })

    // Preview the removal locally, the same way the edit modal does.
    setPhysician(updatedPhysician)
  }

  const loadPhysician = async () => {
    try {
      setLoading(true)
      setError(null)

      if (shared) {
        const response = await sharedPhysiciansAPI.list()
        const record = (response.data.data || []).find(d => d.id === id)
        if (!record) throw new Error('Physician not found in the shared directory')
        // Show this doctor's latest unpublished edit, if any (drafts carry the whole record).
        const { changes } = useSharedDraftStore.getState()
        const latest = [...(changes || [])].reverse().find(c => c.operation === 'update' && c.resource_id === id)
        setPhysician({ ...(latest?.new_data || record), id, _id: id })
        return
      }
      const response = await physicianAPI.get(id)
      const formattedResponse = apiHelpers.formatResponse(response)
      
      setPhysician(formattedResponse.data)
    } catch (err) {
      const errorInfo = apiHelpers.handleError(err)
      setError(errorInfo.message)
      console.error('Error loading physician:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatPhysicianName = (physician) => {
    const { first_name, middle_name, last_name, title } = physician
    const name = [first_name, middle_name, last_name].filter(Boolean).join(' ')
    return title ? `${title} ${name}` : name
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided'
    try {
      return new Date(dateString).toLocaleDateString()
    } catch {
      return dateString // Return as-is if not a valid date
    }
  }

  const formatAddress = (address) => {
    if (!address) return 'Not provided'
    
    // Core stores {line, zip}; older drafts wrote {street, zip_code}.
    const parts = [
      address.line || address.street,
      address.city,
      address.state,
      address.zip || address.zip_code
    ].filter(Boolean)
    
    return parts.join(', ') || 'Not provided'
  }

  const getLicenseStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'text-green-600 bg-green-50'
      case 'expired':
        return 'text-red-600 bg-red-50'
      case 'pending':
        return 'text-yellow-600 bg-yellow-50'
      case 'suspended':
        return 'text-red-600 bg-red-50'
      default:
        return 'text-gray-600 bg-gray-50'
    }
  }

  // Calculate license status for state map
  const calculateLicenseStatus = (licenseData) => {
    if (!licenseData) return null

    const medLicense = licenseData.state?.license
    const deaLicense = licenseData.DEA?.license
    const medExpiry = licenseData.state?.expiry
    const deaExpiry = licenseData.DEA?.expiry

    // Check if medical license exists and is valid
    const hasMedLicense = medLicense && medLicense.trim() && medLicense.toLowerCase() !== 'n/a'
    const hasDeaLicense = deaLicense && deaLicense.trim() && deaLicense.toLowerCase() !== 'n/a'

    if (!hasMedLicense) return null

    const today = new Date()
    const medExpiryDate = medExpiry ? new Date(medExpiry) : null
    const deaExpiryDate = deaExpiry ? new Date(deaExpiry) : null

    // Check expiry status
    const medExpired = medExpiryDate && medExpiryDate < today
    const medExpiringSoon = medExpiryDate && !medExpired && (medExpiryDate - today) / (1000 * 60 * 60 * 24) <= 90
    
    const deaExpired = deaExpiryDate && deaExpiryDate < today
    const deaExpiringSoon = deaExpiryDate && !deaExpired && (deaExpiryDate - today) / (1000 * 60 * 60 * 24) <= 90

    // Determine color priority: red (expired) > yellow (expiring) > purple (DEA current) > green (med current)
    if (medExpired) return 'expired'
    if (medExpiringSoon) return 'expiring'
    if (hasDeaLicense && !deaExpired && !deaExpiringSoon) return 'dea-current'
    return 'med-current'
  }

  const getMapStateColor = (status) => {
    switch (status) {
      case 'expired': return '#dc2626' // red-600
      case 'expiring': return '#ca8a04' // yellow-600
      case 'dea-current': return '#7c3aed' // purple-600
      case 'med-current': return '#16a34a' // green-600
      default: return '#e5e7eb' // gray-200
    }
  }

  // Helper function to get state abbreviation from state name
  const getStateAbbreviation = (stateName) => {
    const stateMap = {
      'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
      'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
      'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
      'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
      'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
      'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
      'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
      'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
      'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
      'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY'
    }
    return stateMap[stateName] || stateName
  }

  // Check if we're still loading auth or if clinic selection is required
  if (authLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    )
  }

  // Only show clinic selection required if auth is loaded and user is not super admin and no clinic selected
  if (!shared && !currentClinicId && user?.role !== 'super_admin') {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-yellow-200 bg-yellow-50 dark:bg-yellow-950 dark:border-yellow-700">
          <CardHeader>
            <CardTitle className="text-yellow-800 dark:text-yellow-200">Clinic Selection Required</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-yellow-800 dark:text-yellow-300">
              Please select a clinic from the dropdown in the header to view physician information.
            </div>
            <div className="mt-4">
              <Link to={basePath}>
                <Button variant="outline">Back to Physicians</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading physician details...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="text-red-800">
              <strong>Error:</strong> {error}
            </div>
            <div className="mt-4">
              <Link to={basePath}>
                <Button variant="outline">Back to Physicians</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!physician) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-muted-foreground">
              Physician not found.
            </div>
            <div className="mt-4 text-center">
              <Link to={basePath}>
                <Button variant="outline">Back to Physicians</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {shared && canEdit && <SharedDraftBar />}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-2">
            <Link to="/physicians" className="hover:text-primary-600">
              Physicians
            </Link>
            <span>›</span>
            <span>{formatPhysicianName(physician)}</span>
          </div>
          <h1 className="text-3xl font-bold tracking-tight">
            {formatPhysicianName(physician)}
          </h1>
          <p className="text-muted-foreground">
            Physician Details
          </p>
        </div>
        <div className="space-x-2">
          <Button 
            variant="outline" 
            onClick={() => navigate(basePath)}
          >
            Back to List
          </Button>
        </div>
      </div>


      <div className="grid gap-6 md:grid-cols-2">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Info</CardTitle>
              {editable && <Button 
                variant="outline" 
                size="sm"
                onClick={() => setIsEditInfoOpen(true)}
              >
                ✏️ Edit
              </Button>}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2">
              <Label className="text-sm font-medium">Full Name</Label>
              <div className="text-sm">{formatPhysicianName(physician)}</div>
            </div>

            <div className="grid gap-2">
              <Label className="text-sm font-medium">NPI Number</Label>
              <div className="text-sm font-mono">{physician.npi || 'Not provided'}</div>
            </div>

            <div className="grid gap-2">
              <Label className="text-sm font-medium">Prefix</Label>
              <div className="text-sm">{physician.prefix || 'Not provided'}</div>
            </div>

            <div className="grid gap-2">
              <Label className="text-sm font-medium">Date of Birth</Label>
              <div className="text-sm">{physician.dob ? formatDate(physician.dob) : 'Not provided'}</div>
            </div>

            <div className="grid gap-2">
              <Label className="text-sm font-medium">Patient Acceptance</Label>
              <div className="flex flex-wrap gap-2 text-sm">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  physician.accept_new_patients 
                    ? 'text-green-600 bg-green-50' 
                    : 'text-red-600 bg-red-50'
                }`}>
                  New Patients: {physician.accept_new_patients ? 'Yes' : 'No'}
                </span>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  physician.accept_refill_patients 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-red-600 bg-red-50'
                }`}>
                  Refill Patients: {physician.accept_refill_patients ? 'Yes' : 'No'}
                </span>
              </div>
            </div>

            <div className="grid gap-2">
              <Label className="text-sm font-medium">Script Settings</Label>
              <div className="text-sm">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  physician.physician_config?.minimize_scripts 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-600 bg-gray-50'
                }`}>
                  Minimize Scripts: {physician.physician_config?.minimize_scripts ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>

            <div className="grid gap-2">
              <Label className="text-sm font-medium">System IDs</Label>
              <div className="space-y-1 text-sm">
                {physician.physician_dosespot_id && (
                  <div><strong>Dosespot ID:</strong> <span className="font-mono">{physician.physician_dosespot_id}</span></div>
                )}
                {physician.physician_id && (
                  <div><strong>Physician ID:</strong> <span className="font-mono">{physician.physician_id}</span></div>
                )}
                {physician._id && (
                  <div><strong>Database ID:</strong> <span className="font-mono text-xs">{physician._id}</span></div>
                )}
              </div>
            </div>

            {physician.specialties && physician.specialties.length > 0 && (
              <div className="grid gap-2">
                <Label className="text-sm font-medium">Specialties</Label>
                <div className="text-sm">
                  {physician.specialties.map((specialty, index) => (
                    <span 
                      key={index}
                      className="inline-block bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs mr-2 mb-1"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Information */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-sm font-medium mb-2 block">Contact</Label>
              <div className="space-y-2">
                <div className="grid gap-1">
                  <span className="text-xs text-muted-foreground">Email</span>
                  <div className="text-sm">
                    {physician.email ? (
                      <a 
                        href={`mailto:${physician.email}`}
                        className="text-primary hover:underline"
                      >
                        {physician.email}
                      </a>
                    ) : (
                      'Not provided'
                    )}
                  </div>
                </div>

                <div className="grid gap-1">
                  <span className="text-xs text-muted-foreground">Phone</span>
                  <div className="text-sm">
                    {physician.phone ? (
                      <a 
                        href={`tel:${physician.phone}`}
                        className="text-primary hover:underline"
                      >
                        {physician.phone}
                      </a>
                    ) : (
                      'Not provided'
                    )}
                  </div>
                </div>

                <div className="grid gap-1">
                  <span className="text-xs text-muted-foreground">Address</span>
                  <div className="text-sm">{formatAddress(physician.address)}</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* State License Map */}
        <Card>
          <CardHeader>
            <CardTitle>License Map</CardTitle>
            <CardDescription>
              Visual representation of licensed states
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Legend */}
            <div className="flex flex-wrap gap-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#16a34a' }}></div>
                <span>Medical Current</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#7c3aed' }}></div>
                <span>DEA Current</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#eab308' }}></div>
                <span>Expiring (≤90 days)</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#dc2626' }}></div>
                <span>Expired</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#e5e7eb' }}></div>
                <span>No License</span>
              </div>
            </div>
            
            {/* US State Map */}
            <div className="relative border rounded-lg p-4 bg-slate-50 dark:bg-slate-900/50">
              <ComposableMap 
                projection="geoAlbersUsa"
                className="w-full h-64"
                width={800}
                height={500}
              >
                <Geographies geography={geoUrl}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const stateName = geo.properties.NAME
                      const stateCode = getStateAbbreviation(stateName)
                      const licenseData = physician.licenses?.[stateCode]
                      const status = calculateLicenseStatus(licenseData)
                      const color = getMapStateColor(status)
                      
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill={color}
                          stroke="#374151"
                          strokeWidth={0.5}
                          style={{
                            default: {
                              outline: "none",
                            },
                            hover: {
                              fill: color,
                              stroke: "#374151",
                              strokeWidth: 2,
                              outline: "none",
                            },
                            pressed: {
                              fill: color,
                              stroke: "#374151",
                              strokeWidth: 2,
                              outline: "none",
                            },
                          }}
                        >
                          {status && (
                            <title>
                              {stateCode}: {licenseData?.state?.license || 'N/A'}
                              {licenseData?.state?.expiry && ` (exp: ${formatDate(licenseData.state.expiry)})`}
                              {licenseData?.DEA?.license && ` | DEA: ${licenseData.DEA.license}`}
                            </title>
                          )}
                        </Geography>
                      )
                    })
                  }
                </Geographies>
              </ComposableMap>
            </div>
            
            {/* License Summary */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="font-medium text-muted-foreground">Licensed States:</div>
                <div className="text-lg font-semibold">
                  {physician.active_licenses ? 
                    physician.active_licenses.filter(license => license.type === 'state').length :
                    Object.keys(physician.licenses || {}).filter(state => {
                      const licenseData = physician.licenses[state]
                      return calculateLicenseStatus(licenseData) !== null
                    }).length || 0}
                </div>
              </div>
              <div>
                <div className="font-medium text-muted-foreground">Expiring Soon:</div>
                <div className="text-lg font-semibold text-yellow-600">
                  {physician.expiring_licenses?.length || Object.keys(physician.licenses || {}).filter(state => {
                    const licenseData = physician.licenses[state]
                    return calculateLicenseStatus(licenseData) === 'expiring'
                  }).length || 0}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pay: shared directory editors on the Shared Doctors page only */}
        {shared && showPay && canEdit && (
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Pay <span className="text-base font-normal text-muted-foreground">({compLabel(physician.compensation)})</span></CardTitle>
              <CardDescription>Drafted to the shared directory; publish from the yellow bar above.</CardDescription>
            </CardHeader>
            <CardContent>
              <CompensationEditor record={physician} onSave={(comp) => {
                const next = { ...physician }
                if (comp) next.compensation = comp; else delete next.compensation
                draftStore.getState().addChange({
                  resource_type: 'physician',
                  resource_id: physician._id,
                  operation: 'update',
                  original_data: physician,
                  new_data: next,
                  description: `Pay for ${physician.first_name} ${physician.last_name}: ${compLabel(comp)}`
                })
                setPhysician(next)
              }} />
            </CardContent>
          </Card>
        )}

        {/* Licenses */}
        <Card className="md:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Medical Licenses</CardTitle>
                <CardDescription>
                  State medical licenses and their current status
                </CardDescription>
              </div>
              {editable && <Button 
                variant="outline" 
                size="sm"
                onClick={() => setIsAddLicenseOpen(true)}
              >
                ➕ Add New License
              </Button>}
            </div>
          </CardHeader>
          <CardContent>
            {/* Show ALL licenses from licenses object with proper status calculation */}
            {physician.licenses && typeof physician.licenses === 'object' && Object.keys(physician.licenses).length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 font-medium">State</th>
                      <th className="text-left p-3 font-medium">Medical License</th>
                      <th className="text-left p-3 font-medium">Expiry</th>
                      <th className="text-left p-3 font-medium">Status</th>
                      <th className="text-left p-3 font-medium">DEA License</th>
                      <th className="text-left p-3 font-medium">DEA Expiry</th>
                      <th className="text-left p-3 font-medium">DEA Status</th>
                      <th className="text-left p-3 font-medium">Address</th>
                      <th className="text-left p-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(physician.licenses)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([stateCode, stateData]) => {
                      const stateLicense = stateData?.state?.license
                      const stateExpiry = stateData?.state?.expiry
                      const deaLicense = stateData?.DEA?.license
                      const deaExpiry = stateData?.DEA?.expiry
                      
                      // Calculate actual status for state license
                      const stateStatus = calculateLicenseStatus({ state: stateData?.state })
                      const deaStatus = calculateLicenseStatus({ DEA: stateData?.DEA })
                      
                      // Skip if no valid licenses at all
                      const hasValidStateLicense = stateLicense && stateLicense.toLowerCase() !== 'n/a' && stateLicense.trim() !== ''
                      const hasValidDeaLicense = deaLicense && deaLicense.toLowerCase() !== 'n/a' && deaLicense.trim() !== ''
                      
                      if (!hasValidStateLicense && !hasValidDeaLicense) return null
                      
                      return (
                        <tr key={stateCode} className="border-b hover:bg-muted/50">
                          <td className="p-3 font-medium">{stateCode}</td>
                          <td className="p-3 font-mono text-sm">
                            {hasValidStateLicense ? stateLicense : 'None'}
                          </td>
                          <td className="p-3 text-sm">
                            {hasValidStateLicense ? formatDate(stateExpiry) : 'N/A'}
                          </td>
                          <td className="p-3">
                            {hasValidStateLicense ? (
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getLicenseStatusColor(stateStatus)}`}>
                                {stateStatus === 'expired' ? 'Expired' : 
                                 stateStatus === 'expiring' ? 'Expiring' : 'Active'}
                              </span>
                            ) : (
                              <span className="text-muted-foreground text-sm">No License</span>
                            )}
                          </td>
                          <td className="p-3 font-mono text-sm">
                            {hasValidDeaLicense ? deaLicense : 'None'}
                          </td>
                          <td className="p-3 text-sm">
                            {hasValidDeaLicense ? formatDate(deaExpiry) : 'N/A'}
                          </td>
                          <td className="p-3">
                            {hasValidDeaLicense ? (
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getLicenseStatusColor(deaStatus)}`}>
                                {deaStatus === 'expired' ? 'Expired' : 
                                 deaStatus === 'expiring' ? 'Expiring' : 'Active'}
                              </span>
                            ) : (
                              <span className="text-muted-foreground text-sm">No License</span>
                            )}
                          </td>
                          <td className="p-3 text-sm">{formatAddress(stateData?.address)}</td>
                          <td className="p-3">
                            {editable && <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setEditingLicense({ stateCode, data: stateData })
                                  setIsLicenseModalOpen(true)
                                }}
                              >
                                ✏️ Edit
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-destructive hover:bg-destructive/10"
                                onClick={() => handleDeleteLicense(stateCode)}
                                title={`Remove the ${stateCode} license entirely`}
                              >
                                🗑️ Delete
                              </Button>
                            </div>}
                          </td>
                        </tr>
                      )
                    }).filter(Boolean)}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                No license information available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Configuration */}
        {physician.configurations && Object.keys(physician.configurations).length > 0 && (
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Additional Configurations</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                {Object.entries(physician.configurations).map(([key, value]) => (
                  <div key={key} className="grid gap-2">
                    <Label className="text-sm font-medium capitalize">
                      {key.replace(/_/g, ' ')}
                    </Label>
                    <div className="text-sm">
                      {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : (value || 'Not set')}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Metadata */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>System Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3 text-sm">
              <div className="grid gap-2">
                <Label className="text-xs font-medium text-muted-foreground">Record ID</Label>
                <div className="font-mono text-xs">{physician._id}</div>
              </div>
              <div className="grid gap-2">
                <Label className="text-xs font-medium text-muted-foreground">Created</Label>
                <div>{formatDate(physician.created_at)}</div>
              </div>
              <div className="grid gap-2">
                <Label className="text-xs font-medium text-muted-foreground">Last Modified</Label>
                <div>{formatDate(physician.updated_at)}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Edit Physician Info Modal */}
      <PhysicianEditModal
        isOpen={isEditInfoOpen}
        onClose={() => setIsEditInfoOpen(false)}
        physician={physician}
        {...sharedDraftProps}
        onSave={(updatedData) => {
          // Update local state with draft changes for preview
          setPhysician(prev => ({ ...prev, ...updatedData }))
        }}
      />
      
      {/* Edit License Modal */}
      <LicenseEditModal
        isOpen={isLicenseModalOpen}
        onClose={() => {
          setIsLicenseModalOpen(false)
          setEditingLicense(null)
        }}
        physician={physician}
        stateCode={editingLicense?.stateCode}
        licenseData={editingLicense?.data}
        {...sharedDraftProps}
        onSave={(updatedPhysician) => {
          // Update local state with draft changes for preview
          setPhysician(updatedPhysician)
        }}
      />
      
      {/* Add New License Modal */}
      <LicenseEditModal
        isOpen={isAddLicenseOpen}
        onClose={() => setIsAddLicenseOpen(false)}
        physician={physician}
        stateCode={null}
        licenseData={null}
        {...sharedDraftProps}
        onSave={(updatedPhysician) => {
          // Update local state with draft changes for preview
          setPhysician(updatedPhysician)
        }}
      />
    </div>
  )
}

/**
 * A clinic routing from the shared directory shows that directory's record
 * (its own physician records are unused, and the API refuses edits to them).
 */
export default function PhysicianDetailPage() {
  const { currentClinicId } = useAuthStore()
  const [source, setSource] = useState(null)

  useEffect(() => {
    let cancelled = false
    setSource(null)
    if (!currentClinicId) { setSource({ shared: false }); return }
    physicianAPI.source()
      .then(r => { if (!cancelled) setSource(r.data.data) })
      .catch(() => { if (!cancelled) setSource({ shared: false }) })
    return () => { cancelled = true }
  }, [currentClinicId])

  if (!source) return <div className="container mx-auto p-6 text-sm text-muted-foreground">Loading…</div>
  if (!source.shared) return <PhysicianDetail />
  return <PhysicianDetail shared canEdit={!!source.can_edit_shared} />
}

/** /shared-doctors/:id — a shared-directory doctor, for editors. */
export function SharedPhysicianDetailPage() {
  const { user } = useAuthStore()
  const canEdit = user?.role === 'super_admin' || Boolean(user?.permissions?.canManageSharedPhysicians)
  return <PhysicianDetail shared canEdit={canEdit} basePath="/shared-doctors" showPay />
}
